import { useLimits } from '../state/store';
import { money, pct } from '../lib/format';
import type { ThemeExposure } from '../state/derive';

/* One bar per theme with a marker at the limit (§7.1). */
export function ThemeBars({
  themes, toDisplay, currency,
}: {
  themes: ThemeExposure[];
  toDisplay: (usd: number) => number;
  currency: 'USD' | 'PLN';
}) {
  const limits = useLimits();
  if (themes.length === 0) return <div className="empty">No themes tagged yet.</div>;

  return (
    <div className="rows">
      {themes.map((t) => {
        const over = t.weight > limits.themeLimit;
        /* Leverage on a member company rides on the theme too, and the bar
           counts shares. Where the two readings differ the second one is said
           in words under the bar rather than drawn into it: the bar is the
           figure the limit has always been measured against, and quietly
           swapping what it plots would change the meaning of every theme on
           the screen. */
        const leveraged = Math.abs(t.exposureWeight - t.weight) >= 0.05;
        const overExposed = leveraged && t.exposureWeight > limits.themeLimit && !over;
        const scaleMax = Math.max(limits.themeLimit * 1.3, t.weight * 1.05);
        return (
          <div key={t.name} className="card theme-row">
            <div className="theme-head">
              <span className="name">{t.name}</span>
              {over ? <span className="tag" data-kind="over">OVER</span> : null}
              <span className="pctv num">{pct(t.weight)}</span>
            </div>
            <div
              className="cap-bar"
              role="meter"
              aria-valuenow={Number(t.weight.toFixed(1))}
              aria-valuemin={0}
              aria-valuemax={limits.themeLimit}
              aria-label={`${t.name}: ${t.weight.toFixed(1)} percent against a limit of ${limits.themeLimit} percent`}
            >
              <div className="cap-fill" style={{
                width: `${Math.min(100, (t.weight / scaleMax) * 100)}%`,
                background: over ? 'var(--crit)' : 'var(--accent)',
              }} />
              <div className="cap-mark" data-breached={over}
                   style={{ left: `${(limits.themeLimit / scaleMax) * 100}%` }} />
            </div>
            <div className="theme-tickers">
              {money(toDisplay(t.valueUsd), currency)} · {t.tickers.join(' · ')}
            </div>
            {leveraged ? (
              <div className="theme-tickers" style={{ marginTop: 3 }}>
                {pct(t.exposureWeight)} with the contracts counted
                {overExposed ? `, past your ${pct(limits.themeLimit)} limit` : ''}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
